/*
errors.ts
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import axios, { AxiosError } from "axios";
import { errorTypes } from "../constants/errorTypes";

export type ErrorType = {
    titleKey: string;
    keys: string[] | ErrorKey[];
    status?: string | number;
    code?: string | number;
    backToMain?: boolean;
    continueButton?: boolean;
    exitApp?: boolean;
    autoclose?: boolean;
    bottomText?: string
};
export interface ErrorKey{
    key:string
    listItems?: string[]
    variables?: Record<string, unknown>;
}



export const getErrorByName = (errorName: string | unknown): ErrorType | undefined => {
    return errorTypes[Object.keys(errorTypes).find(e => errorTypes[e].name === errorName) ?? ""];
};

export const getErrorByStatusCode = (statusCode: string | number): ErrorType => {
    switch (statusCode) {
        case -1:
            return errorTypes.networkUnavailable;
        default:
            return errorTypes[Object.keys(errorTypes).find(e => errorTypes[e].status === statusCode) ?? ""] ??
                errorTypes[Object.keys(errorTypes).find(e => errorTypes[e].code === statusCode) ?? ""] ??
                errorTypes.errorHasOccured;
    }
};


type StatusOrCodeInfo = {
  axiosError: boolean
  statusOrCode: string | number
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getStatusOrCodeFromError = (err: any): StatusOrCodeInfo => {
  // Default fallback
  let statusOrCode: string | number = -1
  let axiosError = false

  if (isAxiosError(err)) {
    console.log("found axios error")
    console.error(err)
    axiosError = true
    statusOrCode = err?.response?.status ?? -1
    return { axiosError, statusOrCode }
  }
  if (err && typeof err === 'object') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anyErr = err as any

    if (
      typeof anyErr.statusCode === 'number' ||
      typeof anyErr.statusCode === 'string'
    ) {
      statusOrCode = anyErr.statusCode
      return { axiosError, statusOrCode }
    }

    if (typeof anyErr.code === 'number' || typeof anyErr.code === 'string') {
      statusOrCode = anyErr.code
      return { axiosError, statusOrCode }
    }

    if (typeof anyErr.message === 'string') {
      statusOrCode = anyErr.message
      return { axiosError, statusOrCode }
    }
  }
  if (typeof err === 'string' || typeof err === 'number') {
    statusOrCode = err
  }

  return { axiosError, statusOrCode }
}



export const ecommerceSummaryError = (r: { requestStatusCodeTranslation?: string }): Promise<never> => {
    return Promise.reject({
        response: {
            status: 'FM_002',
            message: r.requestStatusCodeTranslation
                ? r.requestStatusCodeTranslation
                : 'Unknown error'
        }
    });
};

export const isAxiosError = (err: unknown): err is AxiosError => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyErr = err as any
  if (axios && typeof axios.isAxiosError === 'function' && axios.isAxiosError(err)) {
    return true
  }
  //fallback
  return !!(
    anyErr &&
    typeof anyErr === 'object' &&
    (
      anyErr.isAxiosError === true || // Axios usually sets this
      anyErr.config ||                // request config
      anyErr.request ||               // XHR / request object
      anyErr.response                 // HTTP response
    )
  )
}